import {
  AddressBook,
  ArrowsMerge,
  Bell,
  BookOpenText,
  Briefcase,
  CalendarDots,
  ClockCounterClockwise,
  Compass,
  Factory,
  Files,
  FunnelSimple,
  GraduationCap,
  type Icon,
  IdentificationBadge,
  LinkedinLogo,
  ListStar,
  MagnifyingGlass,
  MapPin,
  SquaresFour,
  TextAlignLeft,
  ThumbsUp,
  Translate,
  Trash,
  UserCircleMinusIcon,
  UserCirclePlus,
  UserList,
  UserSwitchIcon,
  UsersThree,
} from "@phosphor-icons/react";

import { LEADS_COLUMNS, LEADS_FILTERS } from "features/leads/constants";

import type { TagVariantType } from "common/components/ui/Tag";
import type {
  KeywordModifiers,
  KeywordTypes,
  LinkedInProfile,
  ProfileSearchParams,
} from "common/types";

import type { JSX } from "react";
import type {
  LeadListSourceType,
  ListLead,
  ListLeadsSearchParams,
  NavigatorFilterMeta,
} from "./types";

import ViewProfileFull from "assets/icons/view-profile-full.svg?react";

//posts are shared in two url formats - linkedin.com/feed/update/<urn> and the
//lnkd.in/p/<code> short links. the id is whatever follows the matched path, up
//to a trailing slash, query or hash
export const LINKEDIN_POST_URL_REGEX =
  /^(?:https?:\/\/)?(?:www\.)?(?:linkedin\.com\/feed\/update|lnkd\.in\/p)\/([^/?#]+)/;

export const DEFAULT_LIST_LEADS_SEARCH_PARAMS: ListLeadsSearchParams<never> = {
  keyword: "",
  locations: [],
  current_companies: [],
  past_companies: [],
  schools: [],
  languages: [],
  premium: [],
  source_ids: [],
};

export const LIST_LEADS_FILTERS: {
  [K in keyof Omit<ListLeadsSearchParams<never>, "keyword">]: {
    icon: JSX.Element;
    label: string;
  };
} = {
  current_companies: LEADS_FILTERS.current_companies,
  past_companies: LEADS_FILTERS.past_companies,
  locations: LEADS_FILTERS.locations,
  schools: LEADS_FILTERS.education,
  languages: LEADS_FILTERS.languages,
  premium: { icon: <LinkedinLogo weight="fill" />, label: "Premium" },
  source_ids: { icon: <ArrowsMerge weight="fill" />, label: "Source" },
};

/** Bulk actions for the selected leads, shared by the desktop and mobile UI. */
export const LIST_LEADS_ACTIONS = {
  blacklist: {
    icon: UserCircleMinusIcon,
    getLabel: () => "Blacklist",
    getEligibleItems: (selection: ListLead[]) =>
      selection.filter(
        ({ blacklisted, company_blacklisted }) =>
          !blacklisted && !company_blacklisted,
      ),
  },
  remove: {
    icon: Trash,
    getLabel: () => "Remove",
    getEligibleItems: (selection: ListLead[]) => selection,
    isDanger: true,
  },
  unblacklist: {
    icon: UserSwitchIcon,
    getLabel: () => "Unblacklist",
    getEligibleItems: (selection: ListLead[]) =>
      selection.filter(({ blacklisted }) => blacklisted),
  },
} as const;

/** Bulk actions for the selected excluded leads. */
export const EXCLUDED_LIST_LEADS_ACTIONS = {
  remove: {
    icon: Trash,
    getLabel: () => "Remove",
    getEligibleItems: (selection: LinkedInProfile[]) => selection,
    isDanger: true,
  },
} as const;

/**
 * Single source of truth for the lead list leads columns, consumed by both the
 * desktop table and the mobile list. Every column the leads page already has is
 * taken from there, so the two tables render the same way.
 */
export const LIST_LEADS_COLUMNS = {
  currentCompanies: LEADS_COLUMNS.currentCompanies,
  currentCompaniesTime: LEADS_COLUMNS.currentCompaniesTime,
  pastCompanies: LEADS_COLUMNS.pastCompanies,
  pastCompaniesTime: LEADS_COLUMNS.pastCompaniesTime,
  location: LEADS_COLUMNS.location,
  education: LEADS_COLUMNS.education,
  languages: LEADS_COLUMNS.languages,
} as const;

export const KEYWORD_MODIFIER_OPTIONS: Record<
  KeywordModifiers,
  { tagVariant: TagVariantType; additionalClasses: string }
> = {
  required: {
    tagVariant: "success",
    additionalClasses: "hover:bg-green-100 data-[state=open]:bg-green-100",
  },
  excluded: {
    tagVariant: "danger",
    additionalClasses: "hover:bg-red-100 data-[state=open]:bg-red-100",
  },
  optional: {
    tagVariant: "secondary",
    additionalClasses: "hover:bg-purple-100 data-[state=open]:bg-purple-100",
  },
  raw: {
    tagVariant: "info",
    additionalClasses: "hover:bg-blue-100 data-[state=open]:bg-blue-100",
  },
};

export const KEYWORD_OPTIONS: Record<
  KeywordTypes,
  { label: string; icon: Icon }
> = {
  general: {
    label: "General",
    icon: TextAlignLeft,
  },
  company: {
    label: "Company",
    icon: Briefcase,
  },
  school: {
    label: "School",
    icon: GraduationCap,
  },
  title: {
    label: "Title",
    icon: IdentificationBadge,
  },
};

export const PROFILE_SEARCH_OPTIONS: Record<
  keyof Omit<ProfileSearchParams, "keywords">,
  {
    icon: Icon;
    label: string;
    keywordsKey?: KeywordTypes;
  }
> = {
  locations: {
    label: "Locations",
    icon: MapPin,
  },
  industries: {
    label: "Industries",
    icon: Factory,
  },
  current_companies: {
    label: "Current companies",
    icon: Briefcase,
    keywordsKey: "company",
  },
  past_companies: {
    label: "Past companies",
    icon: ClockCounterClockwise,
    keywordsKey: "company",
  },
  followers_of: {
    label: "Followers of",
    icon: UsersThree,
  },
  schools: {
    label: "Schools",
    icon: GraduationCap,
    keywordsKey: "school",
  },
  skills: { label: "Skills", icon: BookOpenText },
  languages: {
    label: "Profile Languages",
    icon: Translate,
  },
};

export const LEAD_LIST_SOURCE_TYPES: Record<
  LeadListSourceType,
  {
    icon: Icon;
    label: string;
    subtitle: string;
    tagVariant: TagVariantType;
    iconContainerClassName: string;
  }
> = {
  search: {
    icon: MagnifyingGlass,
    label: "LinkedIn search",
    subtitle: "Search all of LinkedIn - 2nd & 3rd degree",
    tagVariant: "teal",
    iconContainerClassName: "border border-teal-200 bg-teal-50 text-teal-700",
  },
  navigator: {
    icon: Compass,
    label: "Sales Navigator",
    subtitle: "Advanced search with Sales Navigator filters",
    tagVariant: "info",
    iconContainerClassName: "border border-blue-200 bg-blue-100 text-blue-600",
  },
  post: {
    icon: ThumbsUp,
    label: "Post engagement",
    subtitle: "People who reacted to or commented on a post",
    tagVariant: "success",
    iconContainerClassName:
      "border border-green-200 bg-green-100 text-green-600",
  },
  event: {
    icon: CalendarDots,
    label: "Event Attendees",
    subtitle: "People attending a LinkedIn event",
    tagVariant: "danger",
    iconContainerClassName: "border border-red-200 bg-red-100 text-red-600",
  },
  group: {
    icon: UserCirclePlus,
    label: "Group Members",
    subtitle: "Members of a LinkedIn group",
    tagVariant: "magenta",
    iconContainerClassName:
      "border border-magenta-200 bg-magenta-100 text-magenta-600",
  },
  list: {
    icon: Files,
    label: "CSV List",
    subtitle: "Import contacts from a CSV file",
    tagVariant: "yellow",
    iconContainerClassName:
      "border border-yellow-200 bg-yellow-100 text-yellow-600",
  },
  crm: {
    icon: SquaresFour,
    label: "Connect CRM",
    subtitle: "Import contacts from your CRM",
    tagVariant: "secondary",
    iconContainerClassName:
      "border border-purple-200 bg-purple-100 text-purple-600",
  },
  drip: {
    icon: AddressBook,
    label: "Search your contacts",
    subtitle: "Search your existing contacts",
    tagVariant: "magenta",
    iconContainerClassName:
      "border border-magenta-200 bg-magenta-100 text-magenta-600",
  },
};

export const NAVIGATOR_FILTER_CATEGORIES: {
  label: string;
  icon: JSX.Element;
  filters: (
    | NavigatorFilterMeta
    | {
        label: string;
        subFilters: NavigatorFilterMeta[];
      }
  )[];
}[] = [
  {
    label: "Companies",
    icon: <Briefcase weight="fill" />,
    filters: [
      {
        label: "Current company",
        selectionType: "includeExclude",
        type: "CURRENT_COMPANY",
        typeaheadType: "COMPANY_WITH_LIST",
        isFixedTypeahead: false,
      },
      {
        label: "Company headcount",
        selectionType: "multiSelect",
        type: "COMPANY_HEADCOUNT",
        typeaheadType: "COMPANY_SIZE",
        isFixedTypeahead: true,
      },
      {
        label: "Past company",
        selectionType: "includeExclude",
        type: "PAST_COMPANY",
        typeaheadType: "COMPANY_WITH_LIST",
        isFixedTypeahead: false,
      },
      {
        label: "Company type",
        selectionType: "multiSelect",
        type: "COMPANY_TYPE",
        typeaheadType: "COMPANY_TYPE",
        isFixedTypeahead: true,
      },
      {
        label: "Company Headquarters location",
        selectionType: "includeExclude",
        type: "COMPANY_HEADQUARTERS",
        typeaheadType: "BING_GEO",
        isFixedTypeahead: false,
      },
    ],
  },
  {
    label: "Role",
    icon: <UserList weight="fill" />,
    filters: [
      {
        label: "Function",
        selectionType: "includeExclude",
        type: "FUNCTION",
        typeaheadType: "FUNCTION",
        isFixedTypeahead: false,
      },
      {
        label: "Current Job title",
        selectionType: "includeExclude",
        type: "CURRENT_TITLE",
        typeaheadType: "TITLE",
        isFixedTypeahead: false,
      },
      {
        label: "Seniority level",
        selectionType: "includeExclude",
        type: "SENIORITY_LEVEL",
        typeaheadType: "SENIORITY_V2",
        isFixedTypeahead: true,
      },
      {
        label: "Past job title",
        selectionType: "includeExclude",
        type: "PAST_TITLE",
        typeaheadType: "TITLE",
        isFixedTypeahead: false,
      },
      {
        label: "Years in current company",
        selectionType: "multiSelect",
        type: "YEARS_AT_CURRENT_COMPANY",
        typeaheadType: "TENURE",
        isFixedTypeahead: true,
      },
      {
        label: "Years in current position",
        selectionType: "multiSelect",
        type: "YEARS_IN_CURRENT_POSITION",
        typeaheadType: "TENURE",
        isFixedTypeahead: true,
      },
    ],
  },
  {
    label: "Personal",
    icon: <IdentificationBadge weight="fill" />,
    filters: [
      {
        label: "Geography",
        subFilters: [
          {
            label: "Region",
            selectionType: "includeExclude",
            type: "REGION",
            typeaheadType: "BING_GEO",
            isFixedTypeahead: false,
          },
          {
            label: "Postal code",
            selectionType: "singleSelect",
            type: "POSTAL_CODE",
            typeaheadType: "BING_GEO_POSTAL_CODE",
            isFixedTypeahead: false,
          },
        ],
      },
      {
        label: "Industry",
        selectionType: "includeExclude",
        type: "INDUSTRY",
        typeaheadType: "INDUSTRY",
        isFixedTypeahead: false,
      },
      {
        label: "First name",
        selectionType: "customValue",
        type: "FIRST_NAME",
      },
      {
        label: "Last name",
        selectionType: "customValue",
        type: "LAST_NAME",
      },
      {
        label: "Profile language",
        selectionType: "multiSelect",
        type: "PROFILE_LANGUAGE",
        typeaheadType: "PROFILE_LANGUAGE",
        isFixedTypeahead: true,
      },
      {
        label: "Years of experience",
        selectionType: "multiSelect",
        type: "YEARS_OF_EXPERIENCE",
        typeaheadType: "TENURE",
        isFixedTypeahead: false,
      },
      {
        label: "Groups",
        selectionType: "singleSelect",
        type: "GROUP",
        typeaheadType: "GROUP",
        isFixedTypeahead: false,
      },
      {
        label: "School",
        selectionType: "includeExclude",
        type: "SCHOOL",
        typeaheadType: "SCHOOL",
        isFixedTypeahead: false,
      },
    ],
  },
  {
    label: "Buyer intent",
    icon: <ViewProfileFull />,
    filters: [
      {
        label: "Following your company",
        type: "FOLLOWS_YOUR_COMPANY",
        selectionType: "boolean",
        value: { id: "CF", text: "Following your company" },
      },
      {
        label: "Viewed your profile recently",
        type: "VIEWED_YOUR_PROFILE",
        selectionType: "boolean",
        value: { id: "VYP", text: "Viewed your profile recently" },
      },
    ],
  },
  {
    label: "Best path in",
    icon: <ListStar weight="fill" />,
    filters: [
      {
        label: "Connection",
        type: "RELATIONSHIP",
        selectionType: "multiSelect",
        typeaheadType: "RELATIONSHIP",
        isFixedTypeahead: true,
      },
      {
        label: "Connections of",
        type: "CONNECTION_OF",
        selectionType: "multiSelect",
        typeaheadType: "CONNECTION_OF",
        isFixedTypeahead: false,
      },
      {
        label: "Past colleague",
        type: "PAST_COLLEAGUE",
        selectionType: "boolean",
        value: { id: "PCOLL", text: "Past colleague" },
      },
      {
        label: "Shared experiences",
        type: "WITH_SHARED_EXPERIENCES",
        selectionType: "boolean",
        value: { id: "COMM", text: "Shared experiences" },
      },
    ],
  },
  {
    label: "Recent updates",
    icon: <Bell weight="fill" />,
    filters: [
      {
        label: "Changed jobs",
        type: "RECENTLY_CHANGED_JOBS",
        selectionType: "boolean",
        value: { id: "RPC", text: "Changed jobs" },
      },
      {
        label: "Posted on LinkedIn",
        type: "POSTED_ON_LINKEDIN",
        selectionType: "boolean",
        value: { id: "RPOL", text: "Posted on LinkedIn" },
      },
      {
        label: "Mentioned in news",
        type: "IN_THE_NEWS",
        selectionType: "boolean",
        value: { id: "MITN", text: "Mentioned in news" },
      },
    ],
  },
  {
    label: "Workflow",
    icon: <FunnelSimple weight="fill" />,
    filters: [
      {
        label: "Account lists",
        selectionType: "includeExclude",
        type: "ACCOUNT_LIST",
        typeaheadType: "ACCOUNT_LIST",
        isFixedTypeahead: false,
      },
      {
        label: "Lead Lists",
        selectionType: "includeExclude",
        type: "LEAD_LIST",
        typeaheadType: "LEAD_LIST",
        isFixedTypeahead: false,
      },
      {
        label: "People you interact with",
        selectionType: "includeExclude",
        type: "LEAD_INTERACTIONS",
        typeaheadType: "LEAD_INTERACTIONS",
        isFixedTypeahead: true,
      },
      {
        label: "Saved leads and accounts",
        selectionType: "includeExclude",
        type: "SAVED_LEADS_AND_ACCOUNTS",
        typeaheadType: "SAVED_LEADS_AND_ACCOUNTS",
        isFixedTypeahead: true,
      },
    ],
  },
] as const;

export const DEFAULT_SEARCH_SOURCE_PARAMS: ProfileSearchParams = {
  keywords: [],
  locations: [],
  industries: [],
  current_companies: [],
  past_companies: [],
  followers_of: null,
  schools: [],
  languages: [],
  skills: [],
};
